import { faFileExcel, faFilePdf, faEnvelope, faCheckSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { topLoaderProgress } from "../../configs";
import { ColumnSelectionsModel, ExportPayload } from "../../models";
import { ApiSchemaFilter } from "../../models/api/filter";
import { exportAsExcel, exportAsPdf, sendEmailReport } from "../../pages/hooks";
import { b64toBlob, downLoadBlob } from "../../utils";
import ModalInputElement from "../modals/common/singleInputModal";
import { toast } from "react-toastify";

type Props = {
  columnSelectionModalShow?: boolean;
  setColumnSelectionModalShow?: (value: boolean) => void;
  columnSelection?: ColumnSelectionsModel[];
  tableType?: number;
  showExcelOption?: boolean;
  showPdfOption?: boolean;
  showColumnSelectionOption?: boolean;
  filterData?: ApiSchemaFilter;
  downloadedFileName?: string;
  callCenterId?: string;
  companyId?: string;
  searchText?: string;
  setLoaderProgress?: any;
  menuClassName?: string;
  onOptionSelect?: () => void;
};

const getColumnList = (columnSelection?: ColumnSelectionsModel[]) => {
  const enabledColumnList = columnSelection?.filter(
    (column: ColumnSelectionsModel) => column.isEnabled
  );

  return (
    enabledColumnList?.map((column: ColumnSelectionsModel) => {
      return {
        name: column.name,
        propertyName: column.propertyName,
      };
    }) ?? []
  );
};

const getExportFilterData = (filterData?: ApiSchemaFilter) => {
  const hasSortQuery =
    Boolean(filterData?.sortQuery?.columnName) ||
    typeof filterData?.sortQuery?.direction === "number";

  return {
    filterQueries: filterData?.filterQueries ?? [],
    sortQuery: hasSortQuery ? filterData?.sortQuery : undefined,
  };
};

export function ExportOptions(props: Props): JSX.Element {
  const {
    setColumnSelectionModalShow,
    columnSelection,
    tableType,
    showExcelOption = true,
    showPdfOption = true,
    showColumnSelectionOption = true,
    filterData,
    downloadedFileName,
    callCenterId,
    companyId,
    searchText,
    setLoaderProgress,
    menuClassName = "dropdown-menu py-0",
    onOptionSelect,
  } = props;
  const [modalShow, setModalShow] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [emailValidationMsg, setEmailValidationMsg] = useState("");

  const handleExportAsExcel = async () => {
    const exportPayload: ExportPayload = {
      searchText: searchText,
      queryRequestModel: getExportFilterData(filterData),
      columnList: getColumnList(columnSelection),
    };
    const responseData = await exportAsExcel({
      tableType,
      exportPayload,
      callCenterId,
      companyId,
      setLoaderProgress,
    });
    if (responseData) {
      const blob = b64toBlob(
        responseData.data,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      );
      downLoadBlob(blob, downloadedFileName!);
    }
  };
  const handleExportAsPdf = async () => {
    const exportPayload: ExportPayload = {
      searchText: searchText,
      queryRequestModel: getExportFilterData(filterData),
      columnList: getColumnList(columnSelection),
    };
    setLoaderProgress(topLoaderProgress.start);
    const responseData = await exportAsPdf({
      tableType,
      exportPayload,
      callCenterId,
      companyId,
      setLoaderProgress,
    });

    if (!responseData?.data || responseData.data.length < 1000) {
      console.error(
        "Export failed. Backend response was not valid base64 PDF.",
        responseData.data
      );
      toast.error("Export failed. Please try again or reduce the export size.");
      setLoaderProgress(topLoaderProgress.complete);
      return;
    }

    try {
      const blob = b64toBlob(responseData.data, "application/pdf");
      downLoadBlob(blob, downloadedFileName!);
    } catch (err) {
      console.error("Failed to decode and download PDF:", err);
      toast.error("Error while processing the PDF.");
    } finally {
      setLoaderProgress(topLoaderProgress.complete);
    }
  };

  const emailReportHandler = async () => {
    const reportPayload: ExportPayload = {
      searchText: searchText,
      queryRequestModel: getExportFilterData(filterData),
      columnList: getColumnList(columnSelection),
    };
    setLoaderProgress(topLoaderProgress.start);
    const responseData = await sendEmailReport(
      tableType!,
      email,
      reportPayload,
      callCenterId ?? "",
      companyId ?? ""
    );
    if (responseData) {
      setLoaderProgress(topLoaderProgress.complete);
    }
  };

  const setReportEmail = (e: any) => {
    if (e.target.value == "") {
      setEmailValidationMsg("");
      setEmail("");
      return;
    }
    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

    if (regex.test(e.target.value)) {
      setEmailValidationMsg("");
      setEmail(e.target.value);
    } else {
      if (e.target.value != "") {
        setEmailValidationMsg("Please enter a valid email address");
        setEmail("");
      } else {
        setEmailValidationMsg("");
      }
    }
  };

  return (
    <>
      <div className={menuClassName}>
        {showExcelOption ? (
          <div className="dropdown-item py-0 px-0">
            <button
              type="button"
              className="btn btn-light text-site text-start w-100 export-options-action"
              onClick={() => {
                onOptionSelect?.();
                handleExportAsExcel();
              }}
            >
              <FontAwesomeIcon
                icon={faFileExcel}
                className="me-2 text-success font-sz-16"
              />
              <span>Export as Excel</span>
            </button>
          </div>
        ) : null}
        {showPdfOption ? (
          <div className="dropdown-item py-0 px-0">
            <button
              type="button"
              className="btn btn-light text-site text-start w-100 export-options-action"
              onClick={() => {
                onOptionSelect?.();
                handleExportAsPdf();
              }}
            >
              <FontAwesomeIcon
                icon={faFilePdf}
                className="me-2 text-danger font-sz-16"
              />
              <span>Export as PDF</span>
            </button>
          </div>
        ) : null}
        {/* <div className="dropdown-item py-0 px-0">
          <a className="btn btn-light text-site text-start w-100" onClick={() => setModalShow(true)}>
            <FontAwesomeIcon
              icon={faEnvelope}
              className="me-2 text-primary font-sz-16"
            />
            <span>Email Report</span>
          </a>
        </div> */}
        {showColumnSelectionOption ? (
          <div className="dropdown-item py-0 px-0">
            <button
              type="button"
              className="btn btn-light text-site text-start w-100 export-options-action"
              onClick={() => {
                onOptionSelect?.();
                if (setColumnSelectionModalShow) {
                  setColumnSelectionModalShow(true);
                }
              }}
            >
              <FontAwesomeIcon
                icon={faCheckSquare}
                className="me-2 text-purple font-sz-16"
              />
              <span>
                Column Selection
              </span>
            </button>
          </div>
        ) : null}
      </div>
      {
        modalShow && <ModalInputElement
          show={modalShow}
          handleShow={setModalShow}
          header={"Email exported items"}
          inputValue={email}
          inputMsg={
            "Please provide the email address where the exported report will be sent to."
          }
          onSaveHandler={emailReportHandler}
          onChangeHandler={setReportEmail}
          type="mail"
          errMsg={emailValidationMsg}
        />
      }
    </>
  );
}
